import { useAnalytics } from '@ecosystem/newron-client-lib';
import { feedback } from '@ecosystem/newron-design-system';
import { ColumnConfig, SortingOptionsType, Table, TableBody } from '@components/design/migrated/Table';
import _ from 'lodash';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { useSelector } from 'react-redux';
import { ShareTypeMapping } from '@const/PerformanceType';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import {
  CategoryLeaderboardGroupingPgKpi,
  CategoryLeaderboardPg,
  ColumnHeaderDisplayNameForCL,
  ColumnHeaderForCL,
} from '@models/CategoryLeaderboard';
import { NoResult } from '@pages/NoResult';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { ChartElementName, ChartElementType } from '@utils/page-analytics/model/ChartInteractionTracking';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import {
  TABLE_CLICKED_TRACKING_TITLE,
  tableClickTrackingFunction,
  TableClickTrackingParam,
  TableName,
} from '@utils/page-analytics/model/TableClickTracking';
import {
  FactRevenue,
  FeatureOption,
  Filters,
  FilterTitle,
  PageName,
  PageType,
  ShareType,
  SubSection,
} from '@utils/pageConfig/Types';
import * as styles from '@utils/tableViewscss/TableViewBoard.module.scss';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { getCategoryLeaderboardColumns } from './category-leaderboard-table-columns/CategoryLeaderboardTableColumns';
import CategoryLeaderboardTableHead from './category-leaderboard-table-head/CategoryLeaderboardTableHead';
import CategoryLeaderboardTableRow from './category-leaderboard-table-row/CategoryLeaderboardTableRow';
import { formatCurrencyLabelColumnConfigList } from '@utils/currencyUtils/CurrencyUtils';
import { CategoryLeaderboardGroupTableRows } from './category-leaderboard-group/CategoryLeaderboardGroupTableRows';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { useCategoryOrSegmentsLeaderboardGroupEventState } from '@components/category-leaderboard/utils/CategoryOrSegmentsLeaderboardGroupContext';
import ErrorResultMessage from '@pages/ErrorResultMessage';

const getTableComponentName = (pageType: PageType) => {
  switch (pageType) {
    case PageType.CATEGORY_LEADERBOARD:
      return TableName.CATEGORY_LEADERBOARD;
    case PageType.EXPLORE:
      return TableName.EXPLORE_LEADERBOARD_TABLE;
    default:
      return '';
  }
};

interface Props {
  isLoading: boolean;
  categoryLeaderboardPg: CategoryLeaderboardPg[];
  selectedShareType: ShareType;
  selectedGrowthType: GrowthTypes;
  loadMore: boolean;
  handleScroll: () => void;
  countries: Filters;
  setIsExportButtonDisabled: Dispatch<SetStateAction<boolean>>;
  factRevenue: FactRevenue;
  categoryLeaderboardGroups: CategoryLeaderboardGroupingPgKpi[];
  currentSortingOption: SortingOptions;
  setCurrentSortingOption: Dispatch<SetStateAction<SortingOptions>>;
  context: GroupContext;
  volume: string;
  totalName: string;
  feature?: FeatureOption;
  isExplorePage?: boolean;
  subSectionForAmplitudeTracking: SubSection;
  doPreserveSelections: boolean;
  isApiError?: boolean;
}

const CategoryGroup: React.FC<Props> = ({
  isLoading,
  categoryLeaderboardPg,
  selectedShareType,
  selectedGrowthType,
  loadMore,
  handleScroll,
  countries,
  setIsExportButtonDisabled,
  factRevenue,
  feature,
  categoryLeaderboardGroups,
  currentSortingOption,
  setCurrentSortingOption,
  context,
  volume,
  totalName,
  isExplorePage,
  subSectionForAmplitudeTracking,
  doPreserveSelections,
  isApiError = false,
}) => {
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const mainFiltersStateRO = useSelector((state: any) => state.mainFiltersRO);
  const pagesConfig = usePageConfig();
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.CATEGORY_LEADERBOARD);
  const { unavailablePgs } = useSelector((state: IRootState) => state.availabilityPg);

  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const { pageType, pageName } = pagesConfig;
  const initialParam: PageElementViewTrackingParam = {
    eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
    sideFilters: sideFiltersState.appliedFilters,
    track: track,
    retailerClientState: retailerClientState,
    pageType,
    pageName,
    componentName: getTableComponentName(pageType),
    elementType: ElementType.TABLE,
    elementKpi: ShareTypeMapping[selectedShareType],
    mainFilterState: mainFiltersStateRO,
    access: pagesConfig.access,
    subSection: subSectionForAmplitudeTracking,
    initialLocationId: getInitialAppliedCountries(pagesConfig, pageType, effectiveProductType),
  };

  const createParamWithData: () => PageElementViewTrackingParam = () => {
    return initialParam;
  };
  const createParamWithoutData: () => PageElementViewTrackingParam = () => {
    return {
      ...initialParam,
      hasData: false,
    };
  };
  const { ref: CategoryLeaderboardTableComponentWithData } = useInViewTracking(createParamWithData, {
    threshold: 0.2,
    triggerOnce: true,
  });
  const { ref: CategoryLeaderboardTableComponentWithoutData } = useInViewTracking(createParamWithoutData, {
    threshold: 0.2,
    triggerOnce: true,
  });

  const { clearSelectedPgsOrSegments, selectedProductGroupOrSegments, setSelectedPgsOrSegments } =
    useCategoryOrSegmentsLeaderboardGroupEventState();
  const memoizedTableContent = React.useMemo(() => JSON.stringify(categoryLeaderboardPg), [categoryLeaderboardPg]);

  const updateSelectedPgsOrSegments = () => {
    const pgOrSegmentItems = categoryLeaderboardPg.map((item) => item.segmentOrPg);
    const itemsToKeepSelected = selectedProductGroupOrSegments?.filter((seg) => pgOrSegmentItems.includes(seg));
    if (itemsToKeepSelected.length > 0) {
      let finalPgsOrSegments = itemsToKeepSelected;
      if (unavailablePgs.length > 0) {
        finalPgsOrSegments = finalPgsOrSegments.filter((pg) => !unavailablePgs.includes(pg));
      }
      setSelectedPgsOrSegments(finalPgsOrSegments);
    } else {
      clearSelectedPgsOrSegments();
    }
  };

  useEffect(() => {
    if (doPreserveSelections) {
      return;
    }

    updateSelectedPgsOrSegments();
  }, [memoizedTableContent]);

  const renderTableBody = () => {
    const total = categoryLeaderboardPg.find((product) => product.segmentOrPg === totalName);
    const productGroups = categoryLeaderboardPg.filter((product) => product.segmentOrPg !== totalName);

    return (
      <>
        {total && (
          <CategoryLeaderboardTableHead
            categoryLeaderboardPg={total}
            columns={columns}
            sortedColumn={currentSortingOption.headerKey}
            selectedGrowthType={selectedGrowthType}
            countries={countries}
            currencySymbol={factRevenue.currencySymbol}
            selectedShareType={selectedShareType}
          />
        )}
        <CategoryLeaderboardGroupTableRows
          categoryLeaderboardGroups={categoryLeaderboardGroups}
          countries={countries}
          currencySymbol={factRevenue.currencySymbol}
          columns={columns}
          sortedColumn={currentSortingOption.headerKey}
          selectedShareType={selectedShareType}
          selectedGrowthType={selectedGrowthType}
          context={context}
          isExplorePage={isExplorePage}
        ></CategoryLeaderboardGroupTableRows>
        <BottomScrollListener onBottom={handleScroll} offset={500}>
          <TableBody>
            {productGroups.map((productGroup) => (
              <CategoryLeaderboardTableRow
                key={productGroup.segmentOrPg}
                categoryLeaderboardPg={productGroup}
                columns={columns}
                sortedColumn={currentSortingOption.headerKey}
                className={styles.TableViewboard__TableRow__WithHover}
                selectedGrowthType={selectedGrowthType}
                countries={countries}
                currencySymbol={factRevenue.currencySymbol}
                selectedShareType={selectedShareType}
                isExplorePage={isExplorePage}
              />
            ))}
          </TableBody>
        </BottomScrollListener>
      </>
    );
  };
  const updateSort = useCallback(
    (options: SortingOptionsType) => {
      //Start Table Clicked Tracking
      //Get the Table Header columns again based on selectedShareType
      const categoryLeaderboardTableHeader = getCategoryLeaderboardColumns(selectedShareType, volume).find(
        (e) => e.headerKey === options.headerKey,
      );
      const trackingParam: TableClickTrackingParam = {
        eventTitle: TABLE_CLICKED_TRACKING_TITLE,
        sideFilters: sideFiltersState.appliedFilters,
        retailerClientState: retailerClientState,
        track: track,
        pageType,
        pageName: retailerClientState.pageViewing,
        componentName: ChartElementName.TABLE_SORT,
        elementType: ChartElementType.SORT,
        optionChosen: `${options.label} ${_.capitalize(options.sortDir)}`,
        ...(categoryLeaderboardTableHeader && { column: `${categoryLeaderboardTableHeader.name}` }),
        access: pagesConfig.access,
      };
      tableClickTrackingFunction(trackingParam);
      //End Table Clicked Tracking
      setCurrentSortingOption(options);
    },
    [setCurrentSortingOption, selectedShareType],
  );

  //

  useEffect(() => {
    if (!categoryLeaderboardPg) return;

    let headerName = ColumnHeaderForCL.categoryGroup; // default case
    if (feature) {
      headerName =
        feature.feature === FilterTitle.CATEGORY ? ColumnHeaderForCL.categoryGroup : ColumnHeaderForCL.market;
    }

    const cols = [
      { headerKey: 'checkbox', name: '', className: 'z-20' },
      {
        headerKey: ColumnHeaderForCL.categoryGroup,
        name: ColumnHeaderDisplayNameForCL(volume)[headerName],
        className: 'z-20',
      },
      ...getCategoryLeaderboardColumns(selectedShareType, volume),
    ];

    setColumns(formatCurrencyLabelColumnConfigList(cols, factRevenue));
  }, [categoryLeaderboardPg, selectedShareType, feature, currentSortingOption]);

  return renderVisualization({
    isLoading: isLoading,
    fetchedApiData: categoryLeaderboardPg,
    defaultData: [],
    jsxToRender: (
      <div data-testid="Category-leaderboard" ref={CategoryLeaderboardTableComponentWithData}>
        <Table
          dontWrap={true}
          columns={columns}
          isHeaderSticky={true}
          sortingOptions={currentSortingOption}
          setSortingOptions={updateSort}
          defaultSortingOptions={currentSortingOption}
          className={styles.TableViewboard__Table}
        >
          {renderTableBody()}
          {loadMore && ( // Not use renderLoading() due colSpan field
            <tbody>
              <tr>
                <td colSpan={columns.length} data-testid="cl-table-loading-icon">
                  <feedback.DotsLoader />
                </td>
              </tr>
            </tbody>
          )}
        </Table>
      </div>
    ),
    noResultHandler: () => setIsExportButtonDisabled(true),
    noResultJsx: <div ref={CategoryLeaderboardTableComponentWithoutData}>{NoResult()}</div>,
    errorMessageJsx: <div ref={CategoryLeaderboardTableComponentWithoutData}>{ErrorResultMessage()}</div>,
    isError: isApiError,
  });
};

export default CategoryGroup;
